import { useGetMe } from 'hooks/api/useGetMe'
import _ from 'lodash'
import { getLEPermissions } from 'src/navigation/pathPermissions'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'

export type permissionFormDataType =
  | 'type'
  | 'allPermissions'
  | 'staffPermissions'
  | 'flexPayPermission'
  | 'compliancePermission'
  | 'gamingReportsPermission'
  | 'playerPaybackPermission'
  | 'patronCheckInsPermission'
  | 'rewardsPermission'
  | 'promotionsPermission'
  | 'sweepstakesPermission'
  | 'enrollInSweepstakesPermission'
  | 'locationSweepstakesPermission'
  | 'playerPaybackReportsPermission'
  | 'locationsPermission'
  | 'leUsersPermission'
  | 'contactUsPermission'
  | 'badgeRewardsPermission'
  | 'badgeLocationsPermission'

export type Permission =
  | 'leUsersPermission'
  | 'liveAtLocationViewPermission'
  | 'locationsPermission'
  | 'patronsAccountsPermission'
  | 'promotionsPermission'
  | 'rewardsPermission'
  | 'sweepstakesPermission'
  | 'locationSweepstakesPermission'
  | 'reportsFinancialPermission'
  | 'reportsNonFinancialPermission'
  | 'flexPayPermission'
  | 'compliancePermission'
  | 'gamingReportsPermission'
  | 'playerPaybackPermission'
  | 'patronCheckInsPermission'
  | 'enrollInSweepstakesPermission'
  | 'playerPaybackReportsPermission'
  | 'contactUsPermission'
  | 'badgeRewardsPermission'
  | 'badgeLocationsPermission'

export const getLEIdsByPermission = (
  meQueryData: any,
  currentCorporateAccountId: number | undefined,
  permission: Permission
): number[] => {
  // Get the LE permissions
  const lePermissions = getLEPermissions(
    meQueryData?.licensedEstablishments,
    meQueryData?.user?.licensedEstablishments,
    currentCorporateAccountId
  )

  // Use lodash to filter LEs where the permission is true, then map to the licensedEstablishmentId
  const ids: number[] = _.map(
    _.filter(lePermissions, [permission, true]),
    'licensedEstablishmentId'
  )

  return ids
}

export const isPermissionOwner = (permission: any): boolean => {
  if (!permission) {
    return false
  }
  const isOwner =
    permission.flexPayPermission &&
    permission.compliancePermission &&
    permission.gamingReportsPermission &&
    permission.playerPaybackPermission &&
    permission.patronCheckInsPermission &&
    permission.rewardsPermission &&
    permission.promotionsPermission &&
    permission.sweepstakesPermission &&
    permission.enrollInSweepstakesPermission &&
    permission.locationSweepstakesPermission &&
    permission.playerPaybackReportsPermission &&
    permission.locationsPermission &&
    permission.leUsersPermission &&
    permission.contactUsPermission &&
    permission.badgeLocationsPermission

  return isOwner
}

export const isPermissionStaff = (permission: any): boolean => {
  if (!permission) {
    return false
  }

  const isStaff =
    !permission.flexPayPermission &&
    permission.compliancePermission &&
    !permission.gamingReportsPermission &&
    permission.playerPaybackPermission &&
    !permission.patronCheckInsPermission &&
    !permission.rewardsPermission &&
    permission.promotionsPermission &&
    !permission.sweepstakesPermission &&
    !permission.enrollInSweepstakesPermission &&
    permission.locationSweepstakesPermission &&
    permission.playerPaybackReportsPermission &&
    !permission.locationsPermission &&
    !permission.leUsersPermission &&
    !permission.contactUsPermission &&
    !permission.badgeRewardsPermission &&
    !permission.badgeLocationsPermission

  return isStaff
}

export const isPermissionCustom = (permission: any): boolean => {
  if (!permission) {
    return false
  }

  const isOwner = isPermissionOwner(permission)
  const isStaff = isPermissionStaff(permission)

  return !isOwner && !isStaff
}

export const hasContactUsPermission = (): boolean => {
  const meQuery = useGetMe()
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'contactUsPermission'
  )

  return leIdsByPermission.length > 0
}
